import React, { memo, useCallback } from 'react'
import { Text, View, ReactionsModal } from '@/components'
import { theme } from '@/config'
import { createStyles } from '@codeleap/styles'
import {
  formatReactions,
  getReactionByAlias,
  getReactionsTotalCount,
  isMessageDeleted,
  getReactionEmoji,
  getLatestReactions,
} from '@packages/chat'
import { useMessageContext, useChannelStateContext } from 'stream-chat-react'
import { MessageProps } from '../types'

export const MessageReactionsPreviewContent = ({ message, isMyMessage }: MessageProps) => {
  const reactionCount = getReactionsTotalCount(message?.reaction_counts)
  const hasReactionCounts = reactionCount > 0
  const reactions = getLatestReactions(message)
  const { channel } = useChannelStateContext()

  const handleClick = useCallback(() => {
    ReactionsModal.open({ message, channel })
  }, [message, channel])

  if (!hasReactionCounts) return null

  const backgroundColor = isMyMessage() ? theme.baseColors.primarySolid300 : theme.baseColors.primarySolid200

  return (
    <View
      style={{
        ...styles.container,
        alignSelf: 'flex-start',
      }}
    >
      <div
        onClick={handleClick}
        data-testid='message-reactions-preview'
        style={{
          ...styles.wrapper,
          backgroundColor,
          cursor: 'pointer',
        }}
      >
        {formatReactions(reactions).map((reaction, index) => {
          const emoji = reaction?.emoji_code || getReactionByAlias(reaction?.type)?.emoji

          return (
            <span key={index} style={styles.emojiText}>
              {getReactionEmoji(emoji)}
            </span>
          )
        })}

        <Text
          text={`${reactionCount}`}
          style={{
            fontSize: 12,
            fontWeight: '600',
            lineHeight: '16px',
            color: theme.baseColors.onPrimarySolid200Heading,
          }}
        />
      </div>
    </View>
  )
}

export const MessageReactionsPreview = () => {
  const props = useMessageContext()
  if (isMessageDeleted(props.message)) return null
  return <MessageReactionsPreviewContent {...props} />
}

const styles = createStyles(theme => ({
  container: {
    display: 'flex',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.radius.small,
    minWidth: 42,
    height: 24,
    padding: '4px 8px',
    gap: 2,
    whiteSpace: 'nowrap',
  },
  emojiText: {
    fontSize: 14,
    lineHeight: '14px',
    display: 'inline-block',
  },
}))
